// Curiosity, not a questionnaire. A companion with no history has to learn who she is, and the only way that
// is not annoying is the way a new friend does it: one question, attached to what she just said, never one she
// already answered, and visibly used later. This module knows what it does not know yet (the gaps in her
// picture), which gap sits next to the current topic, and when to keep quiet: a daily budget, cool-downs on
// topics asked or deflected, and a brake when her replies go short.
import type { Memory, Message, ProfileTopic } from '@/types';

export interface TopicSpec {
  id: ProfileTopic;
  label: string;
  /** Lower asks first: what helps the next conversation most, not what is most informative. */
  priority: number;
  /** Words in her message that make this topic adjacent to what she is talking about. */
  keywords: string[];
  /** Notes' importance summed to this means it knows enough to stop asking. */
  enough: number;
  /** Never asked proactively; only filled when she brings it up. */
  sensitive?: boolean;
}

// Labels are written as the questions a friend would actually have, not as field names: the model turns
// "孩子几岁、喜欢什么" into a question far more readily than "家庭".
export const TOPICS: TopicSpec[] = [
  { id: 'people', label: '她身边的人各自是谁、什么关系、是什么样的人（家人、朋友、同事、孩子的同学和那些家长）', priority: 1, enough: 9, keywords: ['妈', '爸', '婆婆', '公公', '老公', '丈夫', '先生', '姐', '妹', '哥', '弟', '朋友', '闺蜜', '同事', '老板', '领导', '上司', '邻居', '家长', '同学', '家人'] },
  { id: 'family', label: '她的家：孩子几岁、什么性格、喜欢什么、上什么学；家里平时怎么分工、周末怎么过；以前一起去过哪些地方', priority: 1, enough: 8, keywords: ['孩子', '儿子', '女儿', '娃', '老公', '丈夫', '家里', '学校', '同学', '周末', '带他', '带她', '一家', '去过', '动物园', '公园', '玩'] },
  { id: 'comfort', label: '她不开心的时候希望别人怎么做', priority: 2, enough: 4, keywords: ['安慰', '难过', '哭', '委屈', '崩溃', '心情', '陪'] },
  { id: 'triggers', label: '她最不想听到什么话', priority: 2, enough: 4, keywords: ['讨厌', '烦', '受不了', '别说', '说教', '想多了'] },
  { id: 'stress', label: '她主要的压力来自哪里', priority: 2, enough: 6, keywords: ['压力', '焦虑', '担心', '操心', '睡不着', '累', '烦'] },
  { id: 'work', label: '她做什么工作、节奏怎么样、和谁打交道', priority: 3, enough: 6, keywords: ['工作', '公司', '上班', '下班', '加班', '项目', '客户', '开会', '会议', '业务', '单位', '同事', '老板'] },
  { id: 'ongoing', label: '她最近正在忙或操心的事、接下来的安排', priority: 3, enough: 4, keywords: ['最近', '准备', '打算', '计划', '下周', '周日', '周六', '月底', '考试', '搬家', '面试', '装修', '出差', '旅行'] },
  { id: 'daily', label: '她一天怎么过、几点睡、周末做什么', priority: 4, enough: 6, keywords: ['早上', '晚上', '周末', '睡', '起床', '吃', '做饭', '通勤', '健身', '遛', '下班'] },
  { id: 'hobbies', label: '她自己喜欢做什么、看什么、去哪儿放松', priority: 5, enough: 6, keywords: ['喜欢', '爱看', '追剧', '电影', '书', '音乐', '旅行', '运动', '游戏', '画', '手工', '花', '猫', '狗'] },
  { id: 'health', label: '她的身体状况', priority: 5, enough: 4, keywords: ['病', '医生', '医院', '体检', '头疼', '感冒', '疼', '睡不着', '生理期'] },
  { id: 'past', label: '她过去的经历', priority: 9, enough: 6, keywords: ['小时候', '以前', '当年', '那时候', '前任', '老家'], sensitive: true },
];

/** Relation words that name a person without a name; a mention it has no note for is the most natural question there is. */
export const RELATION_WORDS = ['儿子', '女儿', '孩子', '老公', '丈夫', '先生', '妈', '爸', '婆婆', '公公', '姐', '妹', '哥', '弟', '同学', '同事', '老板', '领导', '上司', '朋友', '闺蜜', '邻居', '老师', '对方家长', '家长', '客户'];

export interface Mention {
  name: string;
  /** The person note it already has, if any. */
  known: Memory | null;
}

/** Pure; exported for tests. People her message names (by relation word or by a subject it already knows), and whether it knows them. */
export function mentions(text: string, memories: Memory[]): Mention[] {
  const people = memories.filter((m) => m.kind === 'person');
  const names = new Set<string>();
  for (const m of people) for (const s of m.subjects) if (s && text.includes(s)) names.add(s);
  for (const w of RELATION_WORDS) if (text.includes(w) && ![...names].some((n) => n.includes(w))) names.add(w);
  // "对方家长" contains "家长": keep the longer, more specific mention only.
  const list = [...names].filter((n) => ![...names].some((o) => o !== n && o.includes(n)));
  return list.map((name) => ({ name, known: people.find((m) => m.subjects.some((s) => s === name || s.includes(name) || name.includes(s)) || m.text.includes(name)) ?? null }));
}

export const TOPIC_IDS: ProfileTopic[] = TOPICS.map((t) => t.id);
export const topicById = (id: ProfileTopic): TopicSpec => TOPICS.find((t) => t.id === id)!;

const DAY = 24 * 60 * 60 * 1000;
export const DAILY_BUDGET = 6;
export const ASKED_COOLDOWN_MS = 3 * DAY;
export const DECLINED_COOLDOWN_MS = 7 * DAY;
export const GIVE_UP_AFTER_DECLINES = 2;
const SHORT_REPLY_CHARS = 6;
const BRAKE_PHRASES = ['别问', '不想说', '不说了', '别再问', '不聊这个'];

/** When it asked about a topic and when she deflected one; persisted per browser. */
export interface AskLog {
  asked: Partial<Record<ProfileTopic, number[]>>;
  declined: Partial<Record<ProfileTopic, number[]>>;
}

export const EMPTY_LOG: AskLog = { asked: {}, declined: {} };

/** Pure; exported for tests. How much it knows per topic: notes' importance summed; person notes count as people. */
export function coverage(memories: Memory[]): Record<ProfileTopic, number> {
  const out = Object.fromEntries(TOPIC_IDS.map((id) => [id, 0])) as Record<ProfileTopic, number>;
  for (const m of memories) {
    const topic = m.topic ?? (m.kind === 'person' ? 'people' : undefined);
    if (topic && topic in out) out[topic] += m.importance;
  }
  return out;
}

export function knownTopics(memories: Memory[]): TopicSpec[] {
  const c = coverage(memories);
  return TOPICS.filter((t) => c[t.id] >= t.enough);
}

export function unknownTopics(memories: Memory[]): TopicSpec[] {
  const c = coverage(memories);
  return TOPICS.filter((t) => !t.sensitive && c[t.id] < t.enough);
}

function sameLocalDay(a: number, b: number): boolean {
  const x = new Date(a);
  const y = new Date(b);
  return x.getFullYear() === y.getFullYear() && x.getMonth() === y.getMonth() && x.getDate() === y.getDate();
}

export function askedToday(log: AskLog, now: number): number {
  return Object.values(log.asked).reduce((n, times) => n + (times ?? []).filter((t) => sameLocalDay(t, now)).length, 0);
}

/** Pure; exported for tests. Her last replies went short or she said not to ask: no picture questions this session. */
export function braked(recentUser: Pick<Message, 'text'>[]): boolean {
  const last = recentUser.slice(-2);
  if (last.some((m) => BRAKE_PHRASES.some((p) => m.text.includes(p)))) return true;
  return last.length === 2 && last.every((m) => m.text.trim().length <= SHORT_REPLY_CHARS);
}

export interface Curiosity {
  /** Topics it may get curious about this turn, best first (empty when it should not ask). */
  gaps: TopicSpec[];
  /** People she just named, known or not: the concrete hook for a question. */
  mentions: Mention[];
  /** Why it is holding back, for the prompt to say so explicitly. */
  hold: 'budget' | 'brake' | null;
}

/** Pure; exported for tests. */
export function pickGaps(input: { memories: Memory[]; text: string; recentUser: Pick<Message, 'text'>[]; log: AskLog; now: number; limit?: number }): Curiosity {
  const { memories, text, recentUser, log, now, limit = 4 } = input;
  const named = mentions(text, memories);
  if (braked(recentUser)) return { gaps: [], mentions: named, hold: 'brake' };
  if (askedToday(log, now) >= DAILY_BUDGET) return { gaps: [], mentions: named, hold: 'budget' };
  const cov = coverage(memories);
  const candidates = TOPICS.filter((t) => {
    if (t.sensitive || cov[t.id] >= t.enough) return false;
    const asked = log.asked[t.id] ?? [];
    const declined = log.declined[t.id] ?? [];
    if (declined.length >= GIVE_UP_AFTER_DECLINES) return false;
    if (asked.some((at) => now - at < ASKED_COOLDOWN_MS)) return false;
    if (declined.some((at) => now - at < DECLINED_COOLDOWN_MS)) return false;
    return true;
  });
  const adjacent = (t: TopicSpec) => (t.keywords.some((k) => text.includes(k)) ? 0 : 1);
  candidates.sort((a, b) => adjacent(a) - adjacent(b) || a.priority - b.priority || cov[a.id] - cov[b.id]);
  return { gaps: candidates.slice(0, limit), mentions: named, hold: null };
}

/** Pure; exported for tests. Records topics it asked about or she deflected this turn. */
export function recordAsk(log: AskLog, asked: ProfileTopic[], declined: ProfileTopic[], now: number): AskLog {
  const next: AskLog = { asked: { ...log.asked }, declined: { ...log.declined } };
  for (const t of asked) next.asked[t] = [...(next.asked[t] ?? []), now].slice(-10);
  for (const t of declined) next.declined[t] = [...(next.declined[t] ?? []), now].slice(-10);
  return next;
}

const STORAGE_KEY = 'puppy.askLog';

export function loadAskLog(): AskLog {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return EMPTY_LOG;
    const parsed = JSON.parse(raw) as Partial<AskLog>;
    return { asked: parsed.asked ?? {}, declined: parsed.declined ?? {} };
  } catch {
    return EMPTY_LOG;
  }
}

export function saveAskLog(log: AskLog): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(log));
  } catch {
    // storage blocked: the budget just resets with the page
  }
}

/** The prompts on the "帮它认识你" form: what a friend would want to know first, in her own words, all optional. */
export const INTRO_PROMPTS: { topic: ProfileTopic; question: string }[] = [
  { topic: 'people', question: '你生活里最重要的几个人是谁？他们和你是什么关系？' },
  { topic: 'ongoing', question: '最近让你操心或者在忙的事有哪些？' },
  { topic: 'comfort', question: '你不开心的时候，希望身边的人怎么做？' },
  { topic: 'triggers', question: '最不想听到的话是什么？' },
  { topic: 'daily', question: '平时一天是怎么过的？周末呢？' },
  { topic: 'hobbies', question: '做什么事的时候最放松？' },
];
